import React, { useState } from 'react';
import { ApprovalPolicyStep, ApproverOption, StageAccessDefaults, StagePolicyAccess } from './api';
import './stage-policy-preferences.css';

export interface PolicyPreferences {
  access?: StagePolicyAccess | null;
  notification_user_ids?: string[] | null;
}

export function policyPreferences(policy?: PolicyPreferences): PolicyPreferences {
  return {
    ...(policy?.access !== undefined ? { access: policy.access } : {}),
    ...(policy?.notification_user_ids !== undefined ? { notification_user_ids: policy.notification_user_ids } : {}),
  };
}

function samePeople(a?: string[] | null, b?: string[] | null) {
  if (a == null || b == null) return a == null && b == null;
  return a.length === b.length && a.every(id => b.includes(id));
}

export function samePolicyPreferences(a: PolicyPreferences, b: PolicyPreferences) {
  return (a.access != null) === (b.access != null)
    && samePeople(a.access?.viewer_ids, b.access?.viewer_ids)
    && samePeople(a.access?.editor_ids, b.access?.editor_ids)
    && samePeople(a.notification_user_ids, b.notification_user_ids);
}

interface PeopleFieldProps {
  label: string;
  title?: string;
  value?: string[] | null;
  people: ApproverOption[];
  disabled: boolean;
  defaultLabel: string;
  emptyLabel: string;
  excludedIds?: string[];
  onChange: (value: string[] | null) => void;
  children?: React.ReactNode;
}

function PeopleField({ label, title = label, value, people, disabled, defaultLabel, emptyLabel, excludedIds = [], onChange, children }: PeopleFieldProps) {
  const [query, setQuery] = useState('');
  const selected = value != null;
  const choices = people.filter(person => !excludedIds.includes(person.id));
  const filtered = choices.filter(person => `${person.display_name || ''} ${person.username}`.toLowerCase().includes(query.trim().toLowerCase()));
  const missing = (value || []).filter(id => !people.some(person => person.id === id) && !excludedIds.includes(id));
  return <fieldset className="stage-policy-field" disabled={disabled}>
    <legend>{title}</legend>
    <select aria-label={`${label} access`} value={selected ? 'selected' : 'default'}
      onChange={event => onChange(event.target.value === 'selected' ? [] : null)}>
      <option value="default">{defaultLabel}</option>
      <option value="selected">Specific people</option>
    </select>
    {selected && <>
      {choices.length > 8 && <input type="search" aria-label={`Search ${label.toLowerCase()}`} placeholder="Find a person" value={query} onChange={event => setQuery(event.target.value)} />}
      <div className="stage-policy-people" role="group" aria-label={`${label} people`}>
        {filtered.map(person => <label key={person.id} className={(value || []).includes(person.id) ? 'selected' : ''}>
          <input type="checkbox" checked={(value || []).includes(person.id)}
            onChange={() => onChange((value || []).includes(person.id) ? (value || []).filter(id => id !== person.id) : [...(value || []), person.id])} />
          <span>{person.display_name || person.username}</span>
        </label>)}
        {missing.map(id => <label key={id} className="selected"><input type="checkbox" checked onChange={() => onChange((value || []).filter(valueId => valueId !== id))} /><span>Unavailable account · {id}</span></label>)}
        {!filtered.length && !missing.length && <span className="stage-policy-note">{query ? 'No matching people.' : 'No active Architect accounts.'}</span>}
      </div>
      {!value?.filter(id => !excludedIds.includes(id)).length && <p className="stage-policy-note">{emptyLabel}</p>}
    </>}
    {children}
  </fieldset>;
}

export interface StagePreferenceDraft extends PolicyPreferences {
  ids?: string[];
  ordered?: boolean;
  generalReview?: boolean;
  generalReviewerId?: string | null;
  incompleteNamedApprovers?: boolean;
}
interface Props {
  value: StagePreferenceDraft;
  people: ApproverOption[];
  defaults?: StageAccessDefaults;
  rootIds?: string[];
  step?: ApprovalPolicyStep;
  disabled?: boolean;
  onChange: (patch: PolicyPreferences) => void;
}

/** Preview only. Server membership lists are never written back as explicit access. */
export function previewStageAccess(value: StagePreferenceDraft, people: ApproverOption[], defaults?: StageAccessDefaults, rootIds: string[] = []) {
  const active = (ids: string[]) => Array.from(new Set(ids)).filter(id => people.some(person => person.id === id));
  const editors = value.access?.editor_ids ?? defaults?.editor_ids;
  const approvers = value.generalReview
    ? editors ? [...editors, ...(value.generalReviewerId ? [value.generalReviewerId] : [])] : undefined
    : value.ids?.length ? value.ids : value.incompleteNamedApprovers || value.access != null ? [] : defaults?.approver_ids;
  const directViewers = value.access?.viewer_ids ?? defaults?.viewer_ids;
  return {
    editors: editors ? active([...editors, ...rootIds]) : undefined,
    viewers: directViewers && editors && approvers ? active([...directViewers, ...editors, ...approvers, ...rootIds]) : undefined,
    approvers: approvers ? active([...approvers, ...rootIds]) : undefined,
  };
}

function PeopleResult({ label, ids, people, rootIds = [], empty = 'Root only' }: {
  label: string; ids?: string[]; people: ApproverOption[]; rootIds?: string[]; empty?: string;
}) {
  const visible = ids?.filter(id => !rootIds.includes(id));
  return <div className="stage-policy-result" role="group" aria-label={label}>
    {visible === undefined ? <span className="stage-policy-note">Names unavailable. Reload to check access.</span>
      : visible.length ? <ul className="stage-policy-names">{visible.map(id => {
        const person = people.find(item => item.id === id);
        return <li key={id} title={person?.username}>{person?.display_name || person?.username || 'Unavailable account'}</li>;
      })}</ul> : <span className="stage-policy-note">{empty}</span>}
  </div>;
}

export default function StagePolicyPreferences({ value, people, defaults, rootIds = [], disabled = false, onChange }: Props) {
  const setAccess = (key: keyof StagePolicyAccess, ids: string[] | null) => {
    const access = { viewer_ids: value.access?.viewer_ids ?? null, editor_ids: value.access?.editor_ids ?? null, [key]: ids };
    onChange({ access: access.viewer_ids === null && access.editor_ids === null ? null : access });
  };
  const effective = previewStageAccess(value, people, defaults, rootIds);
  return <div className="stage-policy-preferences">
    <div className="stage-policy-access-grid">
      <PeopleField label="View" title="Can view" value={value.access?.viewer_ids} people={people} excludedIds={rootIds} disabled={disabled}
        defaultLabel="Use team roles" emptyLabel="No additional viewers." onChange={ids => setAccess('viewer_ids', ids)}>
        <PeopleResult label="People who can view" ids={effective.viewers} people={people} rootIds={rootIds} />
      </PeopleField>
      <PeopleField label="Edit" title="Can edit" value={value.access?.editor_ids} people={people} excludedIds={rootIds} disabled={disabled}
        defaultLabel="Use team roles" emptyLabel="No editors. Root retains access." onChange={ids => setAccess('editor_ids', ids)}>
        <PeopleResult label="People who can edit" ids={effective.editors} people={people} rootIds={rootIds} />
      </PeopleField>
    </div>
    <p className="stage-policy-note">View includes editors and approvers.</p>
  </div>;
}

export function StagePolicyNotifications({ value, people, defaults, rootIds = [], step, disabled = false, onChange }: Props) {
  const effective = previewStageAccess(value, people, defaults, rootIds);
  const activeApprovers = value.ids?.filter(id => people.some(person => person.id === id)) || [];
  const automatic = step === 'architecture' ? []
    : value.generalReview ? effective.editors
    : value.ids?.length ? (value.ordered ? activeApprovers.slice(0, 1) : activeApprovers)
    : value.access != null || value.incompleteNamedApprovers ? [] : effective.approvers;
  const requested = value.notification_user_ids ?? automatic;
  const recipients = requested?.length === 0 ? [] : effective.viewers && requested
    ? requested.filter(id => effective.viewers?.includes(id)) : undefined;
  const recipientNames = recipients?.map(id => people.find(person => person.id === id)).filter(Boolean).map(person => person!.display_name || person!.username);
  return <div className="stage-policy-preferences">
    <details className="stage-policy-notifications">
      <summary>Stage-ready notifications <span>{recipientNames === undefined ? 'Names unavailable' : recipientNames.length ? recipientNames.join(', ') : 'Off'}</span></summary>
      <PeopleField label="Notify" title="When this stage is ready for work" value={value.notification_user_ids} people={people} disabled={disabled}
        defaultLabel="Automatic recipients" emptyLabel="No stage-ready notifications." onChange={ids => onChange({ notification_user_ids: ids })} />
      <p className="stage-policy-note">Approval requests follow the review order.</p>
      <p className="stage-policy-note">Telegram must be linked to receive notifications.</p>
      {value.notification_user_ids?.some(id => !effective.viewers?.includes(id)) && effective.viewers && <p className="stage-policy-note">People without view access are excluded.</p>}
    </details>
  </div>;
}
